import { AI_DETECTOR, COLLAB, MARKETPLACE, ONE_STEP_AHEAD, PLANNER, PROFILE, PROJECT_180 } from './preview';

export type PreviewNeed = 'all' | 'plan' | 'teach' | 'grow';
export type CatalogDestination = 'objectives' | 'sites' | 'rubric' | 'oneStepAhead' | 'collab' | 'planner' | 'profile' | 'writingFeedback' | 'aiDetector' | 'jobs';
/** Products that are still ideas: a listing flag each, and nowhere to go. */
export type IdeaId = 'news';
export const IDEA_IDS: readonly IdeaId[] = ['news'];
/** The listing flags. Marketplace and the ideas have a gate but nowhere to go yet. */
export type CatalogGate = CatalogDestination | 'marketplace' | IdeaId;

/** Presentation and discovery metadata; visibility is resolved before filtering. */
export interface CatalogEntry {
  id: string;
  name: string;
  description: string;
  /**
   * Destinations are programs, companions stay with a teacher for a unit, tools do one task.
   * Ideas are named products with no package code and nowhere to go.
   */
  size: 'destination' | 'companion' | 'tool' | 'idea';
  needs: Exclude<PreviewNeed, 'all'>[];
  /** Listed only when this flag is explicitly true. Absent means always listed. */
  gate?: CatalogGate;
  destination?: CatalogDestination;
}

export const PREVIEW_NEEDS: { id: PreviewNeed; label: string }[] = [
  { id: 'all', label: 'Everything' },
  { id: 'plan', label: 'Plan a lesson' },
  { id: 'teach', label: 'In the classroom' },
  { id: 'grow', label: 'Grow as a teacher' },
];

export const PREVIEW_CATALOG: CatalogEntry[] = [
  {
    id: 'writing_feedback', name: 'Writing Feedback',
    description: 'Check tone and communication against a rubric. Revise in your own words and see what changes across drafts.',
    size: 'tool', needs: ['grow', 'teach'], gate: 'writingFeedback', destination: 'writingFeedback',
  },
  {
    id: AI_DETECTOR.id,
    name: AI_DETECTOR.name,
    description: AI_DETECTOR.summary,
    size: 'tool',
    needs: ['teach'],
    gate: 'aiDetector',
    destination: 'aiDetector',
  },
  {
    id: PROJECT_180.id,
    name: PROJECT_180.name,
    description: 'Think, act, and teach like a 20-year veteran. The right frameworks and classroom tools for your first 180 days.',
    size: 'destination',
    needs: ['teach', 'grow'],
  },
  {
    id: MARKETPLACE.id,
    name: MARKETPLACE.name,
    description: MARKETPLACE.summary,
    size: 'destination',
    needs: ['grow'],
    gate: 'marketplace',
  },
  {
    id: 'objectives',
    name: 'Standard Checker',
    description: 'Found a lesson or worksheet? Check it against the standards. Upload it and review the matches, with evidence from the material itself.',
    size: 'tool',
    needs: ['plan'],
    gate: 'objectives',
    destination: 'objectives',
  },
  {
    id: 'one_step_ahead',
    name: ONE_STEP_AHEAD.name,
    description: ONE_STEP_AHEAD.tagline,
    size: 'companion',
    needs: ['plan', 'teach'],
    gate: 'oneStepAhead',
    destination: 'oneStepAhead',
  },
  {
    id: 'sites',
    name: '240 Sites',
    description: 'Make one page for your class and share the link. Turn an idea into an explainer, a warm-up, or a self-checking quiz.',
    size: 'tool',
    needs: ['plan', 'teach'],
    gate: 'sites',
    destination: 'sites',
  },
  {
    id: 'rubric',
    name: 'Rubric Score',
    description: 'Score writing against a rubric, compare human and Jev ratings, and inspect the supporting sentences.',
    size: 'tool',
    needs: ['teach'],
    gate: 'rubric',
    destination: 'rubric',
  },
  {
    id: 'collab',
    name: COLLAB.name,
    description: COLLAB.summary,
    size: 'tool',
    needs: ['plan', 'grow'],
    gate: 'collab',
    destination: 'collab',
  },
  {
    id: PLANNER.id,
    name: PLANNER.name,
    description: PLANNER.summary,
    size: 'tool',
    needs: ['plan'],
    gate: 'planner',
    destination: 'planner',
  },
  {
    id: 'jobs',
    name: 'Florida & Texas Job Map',
    description: 'Florida and Texas teaching jobs, updated daily where sources are available. Coverage is partial—not every district or opening is included. Apply through the original listing.',
    size: 'tool',
    destination: 'jobs',
    needs: ['grow'],
    gate: 'jobs',
  },
  {
    id: PROFILE.id,
    name: PROFILE.name,
    description: PROFILE.summary,
    size: 'tool',
    needs: ['grow'],
    gate: 'profile',
    destination: 'profile',
  },
  {
    id: 'news',
    name: '240 News',
    description: 'Teaching news that is about you. Follow what is happening in your state and down to your own district.',
    size: 'idea',
    needs: ['grow'],
    gate: 'news',
  },
];

/** The same allowlist feeds results and counts; search cannot reveal a hidden tool. */
export function visibleCatalog(visibility: Partial<Record<CatalogGate, boolean>>): CatalogEntry[] {
  return PREVIEW_CATALOG.filter(entry => !entry.gate || visibility[entry.gate] === true);
}

export function filterCatalog(entries: readonly CatalogEntry[], need: PreviewNeed, query: string): CatalogEntry[] {
  const search = query.trim().toLocaleLowerCase();
  return entries.filter(entry =>
    (need === 'all' || entry.needs.includes(need)) &&
    `${entry.name} ${entry.description}`.toLocaleLowerCase().includes(search),
  );
}
